<template>
  <div class="bookings-page pa-4 pa-md-6">
    <div class="page-container">
      <!-- Header -->
      <div class="d-flex flex-wrap align-center justify-space-between mb-4">
        <div class="d-flex align-center">
          <div class="header-icon-box me-3">
            <v-icon color="#082C69" size="24">mdi-ticket-confirmation-outline</v-icon>
          </div>
          <div>
            <h1 class="text-h6 font-weight-bold navy--text mb-0">قائمة الحجوزات</h1>
            <p class="text-caption text-muted mb-0">إدارة ومتابعة جميع حجوزات الرحلات وحالات الدفع والاسترداد</p>
          </div>
        </div>
        <div class="d-flex align-center gap-2 mt-2 mt-sm-0">
          <!-- Export CSV Button -->
          <v-btn
            color="#082C69"
            class="white--text text-none font-weight-bold px-4 rounded-lg csv-btn"
            elevation="1"
            height="36"
            :loading="exportLoading"
            @click="exportCsv"
          >
            <v-icon left size="16">mdi-file-delimited-outline</v-icon>
            تصدير CSV
          </v-btn>
        </div>
      </div>

      <!-- 5 KPI Cards in 1 Row (Compact Grid) -->
      <div class="kpi-grid mb-4">
        <!-- 1. Total Bookings -->
        <v-card class="kpi-card elevation-1 rounded-xl pa-3">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">إجمالي الحجوزات</span>
            <div class="kpi-icon-circle bg-blue-light">
              <v-icon size="16" color="#082C69">mdi-ticket-percent-outline</v-icon>
            </div>
          </div>
          <div class="kpi-value navy--text mb-0">
            {{ summary.total_bookings }}
          </div>
          <div class="kpi-subtext text-muted">
            جميع الحجوزات المسجلة
          </div>
        </v-card>

        <!-- 2. Confirmed Bookings -->
        <v-card class="kpi-card elevation-1 rounded-xl pa-3">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">حجوزات مؤكدة</span>
            <div class="kpi-icon-circle bg-green-light">
              <v-icon size="16" color="#10B981">mdi-check-circle-outline</v-icon>
            </div>
          </div>
          <div class="kpi-value text-green mb-0">
            {{ summary.confirmed_count }}
          </div>
          <div class="kpi-subtext text-muted">
            نشطة وقادمة
          </div>
        </v-card>

        <!-- 3. Cancelled Bookings -->
        <v-card class="kpi-card elevation-1 rounded-xl pa-3">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">حجوزات ملغاة</span>
            <div class="kpi-icon-circle bg-red-light">
              <v-icon size="16" color="#EF4444">mdi-close-circle-outline</v-icon>
            </div>
          </div>
          <div class="kpi-value text-red mb-0">
            {{ summary.cancelled_count }}
          </div>
          <div class="kpi-subtext text-muted">
            تم إلغاؤها
          </div>
        </v-card>

        <!-- 4. Refunded Bookings -->
        <v-card class="kpi-card elevation-1 rounded-xl pa-3">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">حجوزات بها استرداد</span>
            <div class="kpi-icon-circle bg-indigo-light">
              <v-icon size="16" color="#4F46E5">mdi-cash-refund</v-icon>
            </div>
          </div>
          <div class="kpi-value text-indigo mb-0">
            {{ summary.refund_count }}
          </div>
          <div class="kpi-subtext text-muted">
            تم رد المبالغ
          </div>
        </v-card>

        <!-- 5. Needing Follow-up -->
        <v-card class="kpi-card elevation-1 rounded-xl pa-3">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">تحتاج متابعة</span>
            <div class="kpi-icon-circle bg-amber-light">
              <v-icon size="16" color="#F59E0B">mdi-alert-circle-outline</v-icon>
            </div>
          </div>
          <div class="kpi-value text-amber mb-0">
            {{ summary.follow_up_count }}
          </div>
          <div class="kpi-subtext text-muted">
            ملغاة دون استرداد مالي
          </div>
        </v-card>
      </div>

      <!-- Filters Bar Card -->
      <v-card class="filter-card elevation-1 rounded-xl pa-3 mb-4">
        <!-- Row 1: Search & Dropdowns -->
        <v-row dense align="center">
          <!-- Search Input -->
          <v-col cols="12" md="4" lg="3">
            <v-text-field
              v-model="filters.search"
              placeholder="البحث برقم الحجز، العميل، الهاتف، أو الرحلة..."
              outlined
              dense
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              class="filter-field"
              @keyup.enter="applyFilters"
              @click:clear="filters.search = ''; $nextTick(() => applyFilters())"
            ></v-text-field>
          </v-col>

          <!-- Trip Filter -->
          <v-col cols="12" sm="6" md="2" lg="2">
            <v-select
              v-model="filters.planned_trip_id"
              :items="tripOptions"
              item-text="label"
              item-value="id"
              outlined
              dense
              hide-details
              class="filter-field"
            ></v-select>
          </v-col>

          <!-- Route Filter -->
          <v-col cols="12" sm="6" md="2" lg="2">
            <v-select
              v-model="filters.route_id"
              :items="routeOptions"
              item-text="name"
              item-value="id"
              outlined
              dense
              hide-details
              class="filter-field"
            ></v-select>
          </v-col>

          <!-- Booking Status Filter -->
          <v-col cols="12" sm="6" md="2" lg="2">
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              class="filter-field"
            ></v-select>
          </v-col>

          <!-- Payment Method Filter -->
          <v-col cols="12" sm="6" md="2" lg="1.5">
            <v-select
              v-model="filters.payment_method"
              :items="paymentMethodOptions"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              class="filter-field"
            ></v-select>
          </v-col>

          <!-- Refund Status Filter -->
          <v-col cols="12" sm="6" md="2" lg="1.5">
            <v-select
              v-model="filters.refund_status"
              :items="refundStatusOptions"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              class="filter-field"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Row 2: Date Filters & Action Buttons -->
        <v-row dense align="center" class="mt-2 pt-2 border-top-light">
          <!-- Date From -->
          <v-col cols="12" sm="6" md="3" lg="2.5">
            <v-menu
              v-model="dateFromMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.date_from"
                  placeholder="من تاريخ..."
                  outlined
                  dense
                  hide-details
                  readonly
                  clearable
                  prepend-inner-icon="mdi-calendar"
                  v-bind="attrs"
                  v-on="on"
                  class="filter-field"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.date_from"
                no-title
                locale="ar"
                @input="dateFromMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Date To -->
          <v-col cols="12" sm="6" md="3" lg="2.5">
            <v-menu
              v-model="dateToMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.date_to"
                  placeholder="إلى تاريخ..."
                  outlined
                  dense
                  hide-details
                  readonly
                  clearable
                  prepend-inner-icon="mdi-calendar"
                  v-bind="attrs"
                  v-on="on"
                  class="filter-field"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.date_to"
                no-title
                locale="ar"
                @input="dateToMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-spacer></v-spacer>

          <!-- Action Buttons -->
          <v-col cols="12" sm="12" md="4" class="d-flex justify-end gap-2 mt-2 mt-md-0">
            <v-btn
              color="#082C69"
              class="white--text text-none font-weight-bold px-5 rounded-lg action-btn"
              elevation="1"
              height="36"
              :loading="loading"
              @click="applyFilters"
            >
              <v-icon left size="16">mdi-magnify</v-icon>
              بحث وتطبيق
            </v-btn>
            <v-btn
              outlined
              color="#64748B"
              class="text-none font-weight-bold px-4 rounded-lg action-btn bg-white"
              height="36"
              @click="resetFilters"
            >
              <v-icon left size="16">mdi-refresh</v-icon>
              إعادة ضبط
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <!-- Bookings Table Card -->
      <v-card class="table-card elevation-1 rounded-xl overflow-hidden">
        <!-- Table Header Bar -->
        <div class="d-flex align-center justify-space-between px-4 py-3 bg-white border-bottom">
          <div class="d-flex align-center">
            <span class="text-subtitle-2 font-weight-bold navy--text me-2">الحجوزات</span>
            <v-chip x-small class="font-weight-bold cs-trips-chip-active">
              <span class="cs-chip-text">{{ pagination.total }} حجز</span>
            </v-chip>
          </div>
        </div>

        <!-- Loading Bar -->
        <v-progress-linear
          v-if="loading"
          indeterminate
          color="#082C69"
          height="3"
        ></v-progress-linear>

        <!-- Table Body -->
        <v-simple-table class="custom-bookings-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-right" style="width: 45px;">#</th>
                <th class="text-right">رقم الحجز</th>
                <th class="text-right">العميل</th>
                <th class="text-right">رقم الرحلة</th>
                <th class="text-right">المسار</th>
                <th class="text-right">تاريخ الرحلة</th>
                <th class="text-right">وقت الانطلاق</th>
                <th class="text-right">قيمة الحجز</th>
                <th class="text-center">طريقة الدفع</th>
                <th class="text-center">حالة الحجز</th>
                <th class="text-center">حالة الاسترداد</th>
                <th class="text-center" style="width: 90px;">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              <!-- 1. LOADING STATE -->
              <tr v-if="loading && bookings.length === 0">
                <td colspan="12" class="text-center py-8 text-muted">
                  <v-progress-circular indeterminate color="#082C69" size="30" class="mb-2"></v-progress-circular>
                  <div class="text-caption font-weight-medium">جاري تحميل بيانات الحجوزات...</div>
                </td>
              </tr>

              <!-- 2. ERROR STATE -->
              <tr v-else-if="hasError && bookings.length === 0">
                <td colspan="12" class="text-center py-8 text-muted">
                  <v-icon size="40" color="#EF4444" class="mb-2">mdi-alert-circle-outline</v-icon>
                  <div class="text-subtitle-2 font-weight-bold text-red mb-2">تعذر تحميل بيانات الحجوزات</div>
                  <v-btn small color="#082C69" class="white--text rounded-lg" @click="fetchBookings">
                    <v-icon left size="14">mdi-refresh</v-icon>
                    إعادة المحاولة
                  </v-btn>
                </td>
              </tr>

              <!-- 3. EMPTY STATE -->
              <tr v-else-if="bookings.length === 0">
                <td colspan="12" class="text-center py-8 text-muted">
                  <v-icon size="42" color="#CBD5E1" class="mb-2">mdi-ticket-confirmation-outline</v-icon>
                  <div class="text-subtitle-2 font-weight-medium">لا توجد حجوزات مطابقة لمعايير البحث</div>
                </td>
              </tr>

              <!-- 4. SUCCESS ROWS -->
              <tr
                v-else
                v-for="booking in bookings"
                :key="booking.id"
                class="table-row-hover"
              >
                <!-- Index -->
                <td class="text-muted text-caption">{{ booking.index }}</td>

                <!-- Booking Reference -->
                <td>
                  <div class="d-flex align-center">
                    <span class="mono-code font-weight-bold navy--text">{{ booking.booking_ref }}</span>
                    <v-chip
                      v-if="booking.seats_count > 1"
                      x-small
                      class="ms-2 font-weight-bold cs-seats-chip"
                    >
                      {{ booking.seats_count }} مقاعد
                    </v-chip>
                  </div>
                </td>

                <!-- Customer -->
                <td>
                  <div class="customer-info-cell py-1">
                    <div
                      class="font-weight-bold customer-name-link navy--text"
                      @click="goToCustomer(booking.customer_id)"
                    >
                      {{ booking.customer_name }}
                    </div>
                    <div class="text-caption text-muted font-weight-medium">
                      {{ booking.customer_phone }}
                    </div>
                  </div>
                </td>

                <!-- Trip Ref -->
                <td>
                  <v-chip x-small class="font-weight-bold mono-code cs-trip-chip">
                    {{ booking.trip_ref }}
                  </v-chip>
                </td>

                <!-- Route -->
                <td>
                  <span class="text-body-2 font-weight-medium navy--text">
                    {{ booking.route_name }}
                  </span>
                </td>

                <!-- Trip Date -->
                <td>
                  <span class="text-body-2 cs-date-text">{{ booking.trip_date }}</span>
                </td>

                <!-- Departure Time -->
                <td>
                  <span class="text-body-2 cs-time-text">{{ booking.departure_time }}</span>
                </td>

                <!-- Amount -->
                <td>
                  <div class="font-weight-bold navy--text text-body-2">
                    {{ booking.total_amount }} <span class="text-caption text-muted">ج.م</span>
                  </div>
                </td>

                <!-- Payment Method -->
                <td class="text-center">
                  <v-chip
                    x-small
                    class="font-weight-bold"
                    :class="getPaymentMethodChipClass(booking.payment_method)"
                  >
                    <span class="cs-chip-text">{{ booking.payment_method_label }}</span>
                  </v-chip>
                </td>

                <!-- Booking Status -->
                <td class="text-center">
                  <v-chip
                    x-small
                    class="font-weight-bold"
                    :class="getStatusChipClass(booking.status_code)"
                  >
                    <span class="cs-chip-text">{{ booking.status_label }}</span>
                  </v-chip>
                </td>

                <!-- Refund Status -->
                <td class="text-center">
                  <v-chip
                    x-small
                    class="font-weight-bold"
                    :class="getRefundChipClass(booking.refund_status_code)"
                  >
                    <span class="cs-chip-text">{{ booking.refund_status_label }}</span>
                  </v-chip>
                </td>

                <!-- Actions -->
                <td class="text-center">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="#082C69"
                        v-bind="attrs"
                        v-on="on"
                        @click="goToBookingDetails(booking.id)"
                      >
                        <v-icon size="18">mdi-eye-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>عرض تفاصيل الحجز</span>
                  </v-tooltip>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <!-- Pagination Footer -->
        <div class="d-flex flex-wrap align-center justify-space-between px-4 py-2 border-top bg-white">
          <!-- Records Counter & Page Size -->
          <div class="d-flex align-center gap-2">
            <span class="text-caption text-muted font-weight-medium">
              عرض {{ pagination.from }} إلى {{ pagination.to }} من {{ pagination.total }} حجز
            </span>
            <span class="text-muted mx-2">|</span>
            <div class="d-flex align-center">
              <span class="text-caption text-muted me-2">لكل صفحة:</span>
              <v-select
                v-model="filters.per_page"
                :items="[10, 25, 50]"
                dense
                outlined
                hide-details
                class="per-page-select"
                @change="onPerPageChange"
              ></v-select>
            </div>
          </div>

          <!-- Pagination Controls -->
          <div>
            <v-pagination
              v-if="pagination.last_page > 1"
              v-model="filters.page"
              :length="pagination.last_page"
              :total-visible="7"
              circle
              color="#082C69"
              @input="onPageChange"
            ></v-pagination>
          </div>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  name: 'CustomerServiceBookingsIndex',
  data() {
    return {
      loading: false,
      hasError: false,
      exportLoading: false,
      dateFromMenu: false,
      dateToMenu: false,
      summary: {
        total_bookings: 0,
        confirmed_count: 0,
        cancelled_count: 0,
        refund_count: 0,
        follow_up_count: 0,
      },
      bookings: [],
      routeOptions: [{ id: 'all', name: 'المسار: الكل' }],
      tripOptions: [{ id: 'all', label: 'الرحلة: الكل' }],
      statusOptions: [
        { text: 'حالة الحجز: الكل', value: 'all' },
        { text: 'مؤكد', value: 'confirmed' },
        { text: 'جاري', value: 'in_progress' },
        { text: 'مكتمل', value: 'completed' },
        { text: 'ملغي', value: 'cancelled' },
        { text: 'لم يحضر', value: 'no_show' },
      ],
      paymentMethodOptions: [
        { text: 'طريقة الدفع: الكل', value: 'all' },
        { text: 'إلكتروني', value: '0' },
        { text: 'نقدي', value: '1' },
        { text: 'اشتراك', value: '2' },
      ],
      refundStatusOptions: [
        { text: 'حالة الاسترداد: الكل', value: 'all' },
        { text: 'تم الاسترداد', value: 'refunded' },
        { text: 'بانتظار المتابعة', value: 'pending' },
        { text: 'لا يوجد استرداد', value: 'not_refunded' },
      ],
      filters: {
        search: '',
        route_id: 'all',
        planned_trip_id: 'all',
        status: 'all',
        payment_method: 'all',
        refund_status: 'all',
        date_from: null,
        date_to: null,
        per_page: 10,
        page: 1,
      },
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0,
      },
    };
  },
  created() {
    this.fetchBookings();
  },
  methods: {
    async fetchBookings() {
      this.loading = true;
      this.hasError = false;
      try {
        const params = {
          page: this.filters.page,
          per_page: this.filters.per_page,
        };

        if (this.filters.search && this.filters.search.trim()) {
          params.search = this.filters.search.trim();
        }
        if (this.filters.route_id && this.filters.route_id !== 'all') {
          params.route_id = this.filters.route_id;
        }
        if (this.filters.planned_trip_id && this.filters.planned_trip_id !== 'all') {
          params.planned_trip_id = this.filters.planned_trip_id;
        }
        if (this.filters.status && this.filters.status !== 'all') {
          params.status = this.filters.status;
        }
        if (this.filters.payment_method !== null && this.filters.payment_method !== '' && this.filters.payment_method !== 'all') {
          params.payment_method = this.filters.payment_method;
        }
        if (this.filters.refund_status && this.filters.refund_status !== 'all') {
          params.refund_status = this.filters.refund_status;
        }
        if (this.filters.date_from) {
          params.date_from = this.filters.date_from;
        }
        if (this.filters.date_to) {
          params.date_to = this.filters.date_to;
        }

        // NOTE: axios baseURL is already Keys.VUE_APP_API_URL + "/api"
        // Therefore do NOT prefix with /api !
        const response = await axios.get('/customer-service/bookings', { params });
        const resData = response && response.data ? response.data : {};

        if (resData.summary) {
          this.summary = {
            total_bookings: Number(resData.summary.total_bookings || 0),
            confirmed_count: Number(resData.summary.confirmed_count || 0),
            cancelled_count: Number(resData.summary.cancelled_count || 0),
            refund_count: Number(resData.summary.refund_count || 0),
            follow_up_count: Number(resData.summary.follow_up_count || 0),
          };
        }

        if (resData.routes) {
          this.routeOptions = [
            { id: 'all', name: 'المسار: الكل' },
            ...resData.routes,
          ];
        }

        if (resData.planned_trips) {
          this.tripOptions = [
            { id: 'all', label: 'الرحلة: الكل' },
            ...resData.planned_trips,
          ];
        }

        this.bookings = Array.isArray(resData.bookings) ? resData.bookings : [];
        if (resData.pagination) {
          this.pagination = resData.pagination;
        }
      } catch (err) {
        console.error('Error fetching bookings:', err);
        this.hasError = true;
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.filters.page = 1;
      this.fetchBookings();
    },
    resetFilters() {
      this.filters = {
        search: '',
        route_id: 'all',
        planned_trip_id: 'all',
        status: 'all',
        payment_method: 'all',
        refund_status: 'all',
        date_from: null,
        date_to: null,
        per_page: 10,
        page: 1,
      };
      this.fetchBookings();
    },
    onPageChange(page) {
      this.filters.page = page;
      this.fetchBookings();
    },
    onPerPageChange() {
      this.filters.page = 1;
      this.fetchBookings();
    },
    goToBookingDetails(id) {
      this.$router.push(`/customer-service/bookings/${id}`);
    },
    goToCustomer(customerId) {
      if (customerId) {
        this.$router.push(`/customer-service/customers/${customerId}`);
      }
    },
    async exportCsv() {
      this.exportLoading = true;
      try {
        const params = {};
        if (this.filters.search && this.filters.search.trim()) params.search = this.filters.search.trim();
        if (this.filters.route_id && this.filters.route_id !== 'all') params.route_id = this.filters.route_id;
        if (this.filters.planned_trip_id && this.filters.planned_trip_id !== 'all') params.planned_trip_id = this.filters.planned_trip_id;
        if (this.filters.status && this.filters.status !== 'all') params.status = this.filters.status;
        if (this.filters.payment_method !== null && this.filters.payment_method !== 'all') params.payment_method = this.filters.payment_method;
        if (this.filters.refund_status && this.filters.refund_status !== 'all') params.refund_status = this.filters.refund_status;
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;

        // NOTE: axios baseURL is already Keys.VUE_APP_API_URL + "/api"
        const response = await axios.get('/customer-service/bookings/export-csv', {
          params,
          responseType: 'blob',
        });

        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `beepgo-bookings-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error('Error exporting CSV:', err);
      } finally {
        this.exportLoading = false;
      }
    },
    getStatusChipClass(code) {
      switch (code) {
        case 'confirmed':
          return 'cs-status-chip-confirmed';
        case 'in_progress':
          return 'cs-status-chip-in-progress';
        case 'completed':
          return 'cs-status-chip-completed';
        case 'cancelled':
          return 'cs-status-chip-cancelled';
        case 'no_show':
          return 'cs-status-chip-no-show';
        default:
          return 'cs-status-chip-default';
      }
    },
    getPaymentMethodChipClass(method) {
      switch (method) {
        case 1:
          return 'cs-pay-chip-cash';
        case 2:
          return 'cs-pay-chip-pass';
        default:
          return 'cs-pay-chip-wallet';
      }
    },
    getRefundChipClass(code) {
      switch (code) {
        case 'refunded':
          return 'cs-refund-chip-refunded';
        case 'pending':
          return 'cs-refund-chip-pending';
        case 'not_applicable':
          return 'cs-refund-chip-na';
        default:
          return 'cs-refund-chip-none';
      }
    },
  },
};
</script>

<style scoped>
.bookings-page {
  background-color: #F8FAFC;
  min-height: 100vh;
  font-family: 'Cairo', sans-serif !important;
}

.page-container {
  max-width: 1600px;
  margin: 0 auto;
}

.navy--text {
  color: #082C69 !important;
}

.text-muted {
  color: #64748B !important;
}

.text-green {
  color: #10B981 !important;
}

.text-red {
  color: #EF4444 !important;
}

.text-indigo {
  color: #4F46E5 !important;
}

.text-amber {
  color: #D97706 !important;
}

.header-icon-box {
  width: 40px;
  height: 40px;
  background-color: #EEF2F6;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ========================================================
   COMPACT 5-COLUMN KPI GRID
   ======================================================== */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}

@media (max-width: 1264px) {
  .kpi-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  border: 1px solid #E2E8F0 !important;
  background-color: #FFFFFF !important;
  transition: transform 0.15s, box-shadow 0.15s;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(8, 44, 105, 0.06) !important;
}

.kpi-icon-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-blue-light {
  background-color: #EEF2F6;
}

.bg-green-light {
  background-color: #ECFDF5;
}

.bg-red-light {
  background-color: #FEF2F2;
}

.bg-indigo-light {
  background-color: #EEF2FF;
}

.bg-amber-light {
  background-color: #FFFBEB;
}

.kpi-value {
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.2;
}

.kpi-subtext {
  font-size: 0.72rem;
  font-weight: 500;
  margin-top: 2px;
}

/* ========================================================
   FILTER CARD & INPUTS
   ======================================================== */
.filter-card {
  border: 1px solid #E2E8F0 !important;
  background-color: #FFFFFF !important;
}

.filter-field ::v-deep .v-input__control .v-input__slot {
  min-height: 36px !important;
  height: 36px !important;
  border-radius: 8px !important;
  font-size: 0.82rem !important;
  padding: 0 10px !important;
  background-color: #FFFFFF !important;
}

.filter-field ::v-deep .v-input__append-inner,
.filter-field ::v-deep .v-input__prepend-inner {
  margin-top: 6px !important;
}

.border-top-light {
  border-top: 1px solid #F1F5F9;
}

.action-btn {
  height: 36px !important;
  font-size: 0.82rem !important;
}

.csv-btn {
  height: 36px !important;
  font-size: 0.82rem !important;
}

/* ========================================================
   TABLE STYLING
   ======================================================== */
.table-card {
  border: 1px solid #E2E8F0 !important;
  background-color: #FFFFFF !important;
}

.border-bottom {
  border-bottom: 1px solid #E2E8F0;
}

.custom-bookings-table ::v-deep th {
  background-color: #F8FAFC !important;
  color: #334155 !important;
  font-weight: 700 !important;
  font-size: 0.78rem !important;
  border-bottom: 1px solid #E2E8F0 !important;
  height: 44px !important;
}

.custom-bookings-table ::v-deep td {
  border-bottom: 1px solid #F1F5F9 !important;
  height: 52px !important;
  font-size: 0.84rem !important;
}

.table-row-hover:hover {
  background-color: #F8FAFC !important;
}

.mono-code {
  font-family: 'Roboto Mono', monospace;
  font-size: 0.82rem;
}

.customer-name-link {
  cursor: pointer;
  transition: color 0.15s;
}

.customer-name-link:hover {
  color: #1E40AF !important;
  text-decoration: underline;
}

.cs-trip-chip {
  background-color: #F1F5F9 !important;
  border: 1px solid #CBD5E1 !important;
  color: #1E293B !important;
  font-weight: 700 !important;
  font-size: 0.72rem !important;
}

.cs-seats-chip {
  background-color: #EEF2F6 !important;
  border: 1px solid #CBD5E1 !important;
  color: #082C69 !important;
  font-size: 0.7rem !important;
}

.cs-date-text,
.cs-time-text {
  color: #334155 !important;
  font-weight: 600 !important;
  font-size: 0.82rem !important;
}

.per-page-select {
  max-width: 75px;
}

.per-page-select ::v-deep .v-input__control .v-input__slot {
  min-height: 28px !important;
  height: 28px !important;
  padding: 0 6px !important;
  font-size: 0.78rem !important;
}

.border-top {
  border-top: 1px solid #E2E8F0;
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

/* ========================================================
   HIGH CONTRAST CHIPS & BADGES (CRITICAL ACCESSIBILITY)
   ======================================================== */

/* Booking Status Chips */
.cs-status-chip-confirmed {
  background-color: #ECFDF5 !important;
  border: 1px solid #A7F3D0 !important;
}
.cs-status-chip-confirmed ::v-deep .v-chip__content,
.cs-status-chip-confirmed .cs-chip-text {
  color: #065F46 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-status-chip-in-progress {
  background-color: #EFF6FF !important;
  border: 1px solid #BFDBFE !important;
}
.cs-status-chip-in-progress ::v-deep .v-chip__content,
.cs-status-chip-in-progress .cs-chip-text {
  color: #1E40AF !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-status-chip-completed {
  background-color: #F5F3FF !important;
  border: 1px solid #DDD6FE !important;
}
.cs-status-chip-completed ::v-deep .v-chip__content,
.cs-status-chip-completed .cs-chip-text {
  color: #5B21B6 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-status-chip-cancelled {
  background-color: #FEF2F2 !important;
  border: 1px solid #FECACA !important;
}
.cs-status-chip-cancelled ::v-deep .v-chip__content,
.cs-status-chip-cancelled .cs-chip-text {
  color: #991B1B !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-status-chip-no-show {
  background-color: #FFFBEB !important;
  border: 1px solid #FDE68A !important;
}
.cs-status-chip-no-show ::v-deep .v-chip__content,
.cs-status-chip-no-show .cs-chip-text {
  color: #92400E !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-status-chip-default {
  background-color: #F1F5F9 !important;
  border: 1px solid #E2E8F0 !important;
}
.cs-status-chip-default ::v-deep .v-chip__content,
.cs-status-chip-default .cs-chip-text {
  color: #334155 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

/* Payment Method Chips */
.cs-pay-chip-wallet {
  background-color: #EEF2F6 !important;
  border: 1px solid #CBD5E1 !important;
}
.cs-pay-chip-wallet ::v-deep .v-chip__content,
.cs-pay-chip-wallet .cs-chip-text {
  color: #082C69 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-pay-chip-cash {
  background-color: #F0FDF4 !important;
  border: 1px solid #BBF7D0 !important;
}
.cs-pay-chip-cash ::v-deep .v-chip__content,
.cs-pay-chip-cash .cs-chip-text {
  color: #166534 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-pay-chip-pass {
  background-color: #FFF7ED !important;
  border: 1px solid #FFEDD5 !important;
}
.cs-pay-chip-pass ::v-deep .v-chip__content,
.cs-pay-chip-pass .cs-chip-text {
  color: #9A3412 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

/* Refund Status Chips */
.cs-refund-chip-refunded {
  background-color: #ECFDF5 !important;
  border: 1px solid #A7F3D0 !important;
}
.cs-refund-chip-refunded ::v-deep .v-chip__content,
.cs-refund-chip-refunded .cs-chip-text {
  color: #065F46 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-refund-chip-pending {
  background-color: #FFFBEB !important;
  border: 1px solid #FDE68A !important;
}
.cs-refund-chip-pending ::v-deep .v-chip__content,
.cs-refund-chip-pending .cs-chip-text {
  color: #92400E !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-refund-chip-na {
  background-color: #F8FAFC !important;
  border: 1px solid #E2E8F0 !important;
}
.cs-refund-chip-na ::v-deep .v-chip__content,
.cs-refund-chip-na .cs-chip-text {
  color: #64748B !important;
  font-weight: 600 !important;
  opacity: 1 !important;
}

.cs-refund-chip-none {
  background-color: #F1F5F9 !important;
  border: 1px solid #E2E8F0 !important;
}
.cs-refund-chip-none ::v-deep .v-chip__content,
.cs-refund-chip-none .cs-chip-text {
  color: #475569 !important;
  font-weight: 600 !important;
  opacity: 1 !important;
}

.cs-trips-chip-active {
  background-color: #EFF6FF !important;
  border: 1px solid #BFDBFE !important;
}
.cs-trips-chip-active ::v-deep .v-chip__content,
.cs-trips-chip-active .cs-chip-text {
  color: #1E40AF !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}
</style>
